import { Button, Card, Form, Input, Modal, Space, Typography, message } from 'antd'
import { EditOutlined, LinkOutlined } from '@ant-design/icons'
import { useEffect, useState } from 'react'
import { useMutation, useQuery } from 'urql'

import { CairoPlayTitle } from '@components/common/CairoPlayTitle'
import {
  DepositPlatformByLocalityDocument,
  DepositPlatformUpsertDocument,
} from '../services/depositPlatforms.graphql'

type EditForm = {
  platform_url: string
  notes: string
}

type DepositPlatformPanelProps = {
  /** INSEE code of the project's main plot commune. */
  locality: string
  /** Department code of the project's main plot. */
  departmentCode: string
  /** Display name fallback when no platform record exists yet (project city). */
  commune: string
  /** Architects / collaborators may edit the shared city record. */
  canEdit: boolean
}

export const DepositPlatformPanel: React.FC<DepositPlatformPanelProps> = ({
  locality,
  departmentCode,
  commune,
  canEdit,
}) => {
  const [{ data }, reexecute] = useQuery({
    query: DepositPlatformByLocalityDocument,
    variables: { locality },
    pause: !locality,
  })
  const [, upsertPlatform] = useMutation(DepositPlatformUpsertDocument)

  const record = data?.deposit_platforms_by_pk ?? null
  const [isEditing, setIsEditing] = useState(false)
  const [saving, setSaving] = useState(false)
  const [form] = Form.useForm<EditForm>()

  useEffect(() => {
    if (isEditing) {
      form.setFieldsValue({
        platform_url: record?.platform_url ?? '',
        notes: record?.notes ?? '',
      })
    }
  }, [isEditing, record, form])

  const handleSave = async () => {
    // Defensive: a create/update needs the commune key. The parent only renders
    // this panel with a resolved locality, but guard here too so a missing key
    // can never reach the NOT NULL commune/department_code columns (#542).
    if (!locality) return
    const values = await form.validateFields()
    setSaving(true)
    try {
      const result = await upsertPlatform({
        object: {
          locality,
          commune: record?.commune ?? commune,
          department_code: record?.department_code ?? departmentCode,
          platform_url: values.platform_url?.trim() || null,
          notes: values.notes?.trim() || null,
        },
      })
      if (result.error) throw result.error
      message.success('Plateforme enregistrée')
      setIsEditing(false)
      reexecute({ requestPolicy: 'network-only' })
    } catch (error) {
      message.error(
        error instanceof Error ? error.message : "Erreur lors de l'enregistrement"
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card
      title={<CairoPlayTitle title="Plateforme de dépôt" />}
      style={{ margin: '1em 0' }}
      styles={{ body: { padding: '12px' } }}
    >
      <Space direction="vertical" size="small" style={{ width: '100%' }}>
        {record?.platform_url ? (
          <a href={record.platform_url} target="_blank" rel="noreferrer">
            <Space size={4}>
              <LinkOutlined />
              {record.platform_url}
            </Space>
          </a>
        ) : (
          <Typography.Text type="secondary">
            Aucune plateforme renseignée pour cette commune
          </Typography.Text>
        )}

        {record?.notes && (
          <Typography.Paragraph
            style={{ margin: 0, whiteSpace: 'pre-wrap' }}
            type="secondary"
          >
            {record.notes}
          </Typography.Paragraph>
        )}

        {canEdit && locality && (
          <Button
            icon={<EditOutlined />}
            onClick={() => setIsEditing(true)}
            style={{ width: '100%' }}
            data-cy="action--edit-deposit-platform"
          >
            Modifier
          </Button>
        )}
      </Space>

      <Modal
        title={`Plateforme de dépôt — ${record?.commune ?? commune}`}
        open={isEditing}
        onOk={handleSave}
        onCancel={() => setIsEditing(false)}
        confirmLoading={saving}
        okText="Enregistrer"
        cancelText="Annuler"
        okButtonProps={{ 'data-cy': 'action--save-deposit-platform' }}
      >
        <Typography.Paragraph type="secondary">
          Cette information est partagée pour toutes les demandes de la commune.
        </Typography.Paragraph>
        <Form form={form} layout="vertical" requiredMark={false}>
          <Form.Item name="platform_url" label="Lien de la plateforme de dépôt">
            <Input placeholder="https://..." />
          </Form.Item>
          <Form.Item name="notes" label="Notes / références documentaires">
            <Input.TextArea rows={4} spellCheck lang="fr" />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  )
}
